/**
 * CopyButton (Iter 9.47.t) - universal copy-to-clipboard button.
 *
 * Standard component for ANY copy interaction across the platform. Uses the
 * lucide `Copy` icon (overlapping squares) - matches the icon shown on:
 *   - Pipeline /me link + dashboard link
 *   - Admin pilot magic-link rows
 *   - Share blocks across the app
 *
 * Behaviour:
 *   - On click: writes `value` to clipboard, briefly swaps the icon to a
 *     green `Check`, fires a sonner toast.
 *   - Loading state for async value resolvers (pass `getValue` instead of `value`).
 *   - Fully accessible + keyboard-friendly (uses <button>).
 *   - Sized variants: "icon" (icon only) | "default" | "sm" | "lg".
 *
 * Usage:
 *   <CopyButton value="https://phygital.app/abc" />
 *   <CopyButton value={url} label="Copy magic link" />
 *   <CopyButton getValue={async () => fetchMagicLink(userId)} label="/me link" />
 */
import { useState } from "react";
import { Copy, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export default function CopyButton({
  value,
  getValue,
  label,
  successMessage,
  size = "sm",
  variant = "outline",
  iconOnly = false,
  className = "",
  disabled = false,
  testId,
  onCopied,
}) {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (busy || disabled) return;
    setBusy(true);
    try {
      const v = typeof getValue === "function" ? await getValue() : value;
      if (!v) { toast.error("Nothing to copy"); return; }
      await navigator.clipboard.writeText(v);
      setCopied(true);
      toast.success(successMessage || (label ? `${label} copied` : "Copied"));
      onCopied?.(v);
      setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      toast.error("Could not copy");
    } finally {
      setBusy(false);
    }
  };

  const Icon = busy ? Loader2 : (copied ? Check : Copy);
  const iconClassName = cn(
    "w-3.5 h-3.5",
    busy && "animate-spin",
    copied && "text-green-600",
  );

  if (iconOnly) {
    return (
      <button
        type="button"
        onClick={handleCopy}
        disabled={disabled || busy}
        data-testid={testId || "copy-btn"}
        title={label || "Copy"}
        aria-label={label || "Copy"}
        className={cn(
          "inline-flex items-center justify-center rounded-md p-1.5 text-stone-500 hover:text-stone-900 hover:bg-stone-100 transition-colors disabled:opacity-50",
          className,
        )}
      >
        <Icon className={iconClassName} />
      </button>
    );
  }

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      onClick={handleCopy}
      disabled={disabled || busy}
      data-testid={testId || "copy-btn"}
      className={cn("gap-1.5 text-[10px] h-7 px-2", className)}
    >
      <Icon className={iconClassName} />
      {label && <span>{label}</span>}
    </Button>
  );
}
